import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { edrService, ThreatNoticeRow, ThreatNoticeDetail, CompanyOption } from '../services/edr.service';
import { formatPeriod } from '../lib/edrPeriod';
// 공통 규칙을 먼저 싣고, 페이지 전용 규칙이 뒤에서 덮도록 한다
import './EdrShared.css';
import './EdrThreatPage.css';

/**
 * EDR 위협탐지 안내 — PRD [S1]·[S2]
 *
 * 두 탭이 하나의 화면을 이룬다.
 *   · 대시보드   — 탐지 리스트. **회사·기간 미선택이 유효한 초기 상태**이며 전체가 보인다
 *   · 상세 데이터 — 회사 + 탐지일자로 필터. 둘 다 고르기 전에는 아무 화면도 표시하지 않는다
 *
 * 리스트 행을 클릭하면 상세 탭으로 이동하며 그 건의 보고서가 렌더된다.
 */

type TabId = 'dashboard' | 'detail';

const RISK_CLASS: Record<string, string> = {
  높음: 'badge-risk-high',
  중간: 'badge-risk-mid',
  낮음: 'badge-risk-low',
};

/** 정렬 가능 컬럼 — 백엔드 화이트리스트와 일치해야 한다 */
const SORTABLE = ['detectedAt', 'riskLevel', 'detectionScenario', 'detectionNote', 'assetIp', 'companyName'];

const STORAGE_KEY = 'edr-threat-filters';

interface StoredFilters {
  companyName: string;
  period: string;
}

/** 기간·회사 선택은 새로고침 후에도 유지한다 (프로젝트 공통 규칙) */
function loadFilters(): StoredFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as StoredFilters;
  } catch {
    // 저장값이 깨져 있으면 기본값으로 시작한다
  }
  // 위협탐지 안내는 **미선택(전체)이 유효한 초기값**이다.
  // 저장값이 없을 때 임의의 회사를 자동 선택하지 않는다.
  return { companyName: '', period: '' };
}

const EdrThreatPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  const initial = useMemo(loadFilters, []);
  const [companyName, setCompanyName] = useState(initial.companyName);
  const [period, setPeriod] = useState(initial.period);

  const [rows, setRows] = useState<ThreatNoticeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('detectedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 상세 데이터 탭
  const [detailCompany, setDetailCompany] = useState('');
  const [detailDates, setDetailDates] = useState<string[]>([]);
  const [detailDate, setDetailDate] = useState('');
  const [detail, setDetail] = useState<ThreatNoticeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /**
   * 기간 목록은 **서버가 준다** — 등록된 달만 골라야 한다.
   * 최근 12개월을 나열하면 EDR 도입 이전 달까지 선택 가능해져 빈 화면을 보게 된다.
   */
  const [periods, setPeriods] = useState<string[]>([]);

  useEffect(() => {
    edrService
      .getCompanies('EDR_THREAT')
      .then(setCompanies)
      .catch(() => setError('회사 목록을 불러오지 못했습니다.'));

    edrService
      .getThreatNoticePeriods()
      .then(setPeriods)
      .catch(() => setError('조회 가능한 기간을 불러오지 못했습니다.'));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ companyName, period }));
    } catch {
      // 저장 실패는 기능에 영향을 주지 않는다
    }
  }, [companyName, period]);

  const loadNotices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await edrService.getThreatNotices({
        companyName: companyName || undefined,
        period: period || undefined,
        sort,
        order,
        page,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch {
      setError('탐지 리스트를 불러오지 못했습니다.');
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [companyName, period, sort, order, page]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  /** 정렬을 바꾸면 1페이지로 돌아간다 — 유지하면 페이지 경계가 어긋난다 */
  const toggleSort = (column: string) => {
    if (!SORTABLE.includes(column)) return;

    if (sort === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(column);
      setOrder('desc');
    }
    setPage(1);
  };

  const sortIndicator = (column: string) => {
    if (sort !== column) return ' ↕';
    return order === 'asc' ? ' ▲' : ' ▼';
  };

  const loadDetailDates = useCallback(async (company: string) => {
    if (!company) {
      setDetailDates([]);
      setDetailDate('');
      setDetail(null);
      return;
    }
    try {
      const dates = await edrService.getDetectedDates(company);
      setDetailDates(dates);
    } catch {
      setDetailDates([]);
    }
  }, []);

  const openDetail = async (row: ThreatNoticeRow) => {
    setActiveTab('detail');
    setDetailCompany(row.companyName);
    await loadDetailDates(row.companyName);

    // API는 이미 'YYYY-MM-DD' 문자열을 준다 — 자르면 형식 변경에 조용히 깨진다
    const date = row.detectedDate;
    setDetailDate(date);
    setDetailLoading(true);
    try {
      setDetail(await edrService.getThreatNoticeDetail(row.id));
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
    window.scrollTo(0, 0);
  };

  /** 회사·탐지일자를 직접 고른 경우 — 그 조건의 첫 건을 렌더한다 */
  const loadDetailByFilter = useCallback(async (company: string, date: string) => {
    if (!company || !date) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const list = await edrService.getThreatNotices({ companyName: company });
      const match = list.rows.find((r) => r.detectedDate === date);
      setDetail(match ? await edrService.getThreatNoticeDetail(match.id) : null);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="edr-threat-page">
      <div className="page-header">
        <h1>EDR 위협탐지 안내</h1>
        {/*
          상세 데이터 탭은 **회사 + 탐지일자**로 필터한다(요구사항).
          그 탭에서 상단 회사·기간 필터를 함께 두면 같은 이름의 필터가 두 벌 생겨
          어느 쪽이 적용된 것인지 알 수 없다. 대시보드 탭에서만 보여준다.
        */}
        {activeTab === 'dashboard' && (
        <div className="header-controls">
          <div className="filter-group">
            <label htmlFor="edr-company">회사:</label>
            <select
              id="edr-company"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setPage(1);
              }}
              disabled={isLoading}
            >
              <option value="">전체</option>
              {companies.map((c) => (
                <option key={c.orgCode} value={c.companyName}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="edr-period">기간:</label>
            <select
              id="edr-period"
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                setPage(1);
              }}
              disabled={isLoading}
            >
              <option value="">전체</option>
              {periods.map((p) => (
                <option key={p} value={p}>
                  {formatPeriod(p)}
                </option>
              ))}
            </select>
          </div>
        </div>
        )}
      </div>

      <div className="tab-container">
        <button
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          대시보드
        </button>
        <button
          className={`tab-button ${activeTab === 'detail' ? 'active' : ''}`}
          onClick={() => setActiveTab('detail')}
        >
          상세 데이터
        </button>
      </div>

      <div className="tab-content">
        {error && <div className="publish-warning">{error}</div>}

        {activeTab === 'dashboard' && (
          <div className="tab-panel">
            <h2>🔍 대시보드</h2>
            <hr className="section-divider" />

            <div className="section-block">
              <div className="section-header-row">
                <div className="section-header">
                  <span className="section-icon">📋</span>
                  <span className="edr-section-title">탐지 리스트</span>
                </div>
                <span className="text-muted section-count">
                  전체 {total.toLocaleString()}건 · 50건씩 표시
                </span>
              </div>

              <div className="policy-table-container">
                <table className="policy-table">
                  <thead>
                    <tr>
                      <th className="cell-center" style={{ width: '56px' }}>No</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('detectedAt')}>
                        탐지일자{sortIndicator('detectedAt')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('riskLevel')}>
                        위험도{sortIndicator('riskLevel')}
                      </th>
                      <th
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleSort('detectionScenario')}
                      >
                        진단명{sortIndicator('detectionScenario')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('detectionNote')}>
                        행위{sortIndicator('detectionNote')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('assetIp')}>
                        IP{sortIndicator('assetIp')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('companyName')}>
                        계열사{sortIndicator('companyName')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr className="empty-row">
                        <td colSpan={7}>불러오는 중…</td>
                      </tr>
                    )}
                    {!isLoading && rows.length === 0 && (
                      <tr className="empty-row">
                        <td colSpan={7}>데이터가 없습니다.</td>
                      </tr>
                    )}
                    {!isLoading &&
                      rows.map((row, index) => (
                        <tr
                          key={row.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openDetail(row)}
                          title="클릭하면 상세 데이터로 이동합니다"
                        >
                          <td className="cell-center">{(page - 1) * 50 + index + 1}</td>
                          <td className="cell-center">{row.detectedDate.slice(5)}</td>
                          <td className="cell-center">
                            <span className={`inline-badge ${RISK_CLASS[row.riskLevel] ?? ''}`}>
                              {row.riskLevel}
                            </span>
                          </td>
                          <td className="mono">{row.detectionScenario ?? '—'}</td>
                          <td>{row.detectionNote ?? '—'}</td>
                          <td className="mono cell-center">
                            {row.assetIp ?? '—'}
                          </td>
                          <td>{row.companyName}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                    ‹
                  </button>
                  <span className="current">
                    {page} / {totalPages}
                  </span>
                  <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'detail' && (
          <div className="tab-panel">
            <h2>📄 상세 데이터</h2>
            <hr className="section-divider" />

            <div className="report-management-controls">
              <div className="control-group">
                <label htmlFor="edr-detail-company">회사:</label>
                <select
                  id="edr-detail-company"
                  value={detailCompany}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setDetailCompany(value);
                    setDetailDate('');
                    setDetail(null);
                    await loadDetailDates(value);
                  }}
                >
                  <option value="">선택하세요</option>
                  {companies.map((c) => (
                    <option key={c.orgCode} value={c.companyName}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="control-group">
                <label htmlFor="edr-detail-date">탐지일자:</label>
                <select
                  id="edr-detail-date"
                  value={detailDate}
                  onChange={(e) => {
                    setDetailDate(e.target.value);
                    loadDetailByFilter(detailCompany, e.target.value);
                  }}
                  disabled={!detailCompany}
                >
                  <option value="">
                    {detailCompany ? '선택하세요' : '회사를 먼저 선택하세요'}
                  </option>
                  {detailDates.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 회사·탐지일자를 고르기 전에는 아무 화면도 표시하지 않는다 (요구사항 명시) */}
            {(!detailCompany || !detailDate) && (
              <div className="empty-state">회사와 탐지일자를 선택하세요.</div>
            )}

            {detailCompany && detailDate && detailLoading && (
              <div className="empty-state">불러오는 중…</div>
            )}

            {detailCompany && detailDate && !detailLoading && !detail && (
              <div className="empty-state">해당 조건의 위협탐지 안내가 없습니다.</div>
            )}

            {detail && !detailLoading && (
              <div className="section-block">
                <div className="section-header-row">
                  <div className="section-header">
                    <span className="section-icon">📋</span>
                    <span className="edr-section-title">
                      위협 탐지 안내 — {detail.companyName} / {detail.detectedDate}
                    </span>
                  </div>
                  <button
                    className="link-button"
                    onClick={() => {
                      setActiveTab('dashboard');
                      window.scrollTo(0, 0);
                    }}
                  >
                    ← 탐지 리스트로
                  </button>
                </div>

                {detail.parseStatus === 'PARTIAL' && (
                  <div className="publish-warning">
                    일부 항목이 누락된 안내입니다 (누락: {detail.missingFields ?? '—'})
                  </div>
                )}

                {/*
                  등록 HTML은 외부에서 작성된 입력이다. 서버가 살균한 본문만 렌더한다.
                  원본(html_raw)은 응답에 포함되지 않는다.
                */}
                <div className="edr-notice-frame">
                  <div
                    className="edr-notice-body"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: detail.htmlSanitized }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EdrThreatPage;
